import { useApolloClient } from '@apollo/client';
import { Formik, type FormikProps } from 'formik';
import { useCallback, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type ModelConfiguratorSpecsFragment } from '../../../api/fragments/ModelConfiguratorSpecs';
import { type VariantConfiguratorSpecsFragment } from '../../../api/fragments/VariantConfiguratorSpecs';
import {
    type CopyVariantConfiguratorMutation,
    type CopyVariantConfiguratorMutationVariables,
    CopyVariantConfiguratorDocument,
} from '../../../api/mutations/copyVariantConfigurator';
import { useListVariantConfiguratorsQuery } from '../../../api/queries/listVariantConfigurators';
import { ModuleType, VehicleKind } from '../../../api/types';
import Form from '../../../components/fields/Form';
import VehicleSelectField from '../../../components/fields/VehicleSelectField';
import { useThemeComponents } from '../../../themes';
import useHandleError from '../../../utilities/useHandleError';

type CreateVariantConfiguratorModalProps = {
    isVisible: boolean;
    onSuccess: (variantConfigurator: VariantConfiguratorSpecsFragment) => void;
    onCancel: () => void;
    modelConfigurator: ModelConfiguratorSpecsFragment;
};

type FormValues = {
    oldVariantConfiguratorId: string;
    newVariantConfiguratorId: string;
};

const FORM_NAME = 'copyVariantConfigurator';

const CopyVariantConfiguratorModal = ({
    isVisible,
    onSuccess,
    onCancel,
    modelConfigurator,
}: CreateVariantConfiguratorModalProps) => {
    const { t } = useTranslation(['modelConfiguratorDetails']);
    const { Modal } = useThemeComponents();

    const formRef = useRef<FormikProps<FormValues>>();

    const [isSubmitting, setSubmitting] = useState(false);

    const apolloClient = useApolloClient();

    const handleOkay = useCallback(() => {
        setSubmitting(true);

        formRef.current.handleSubmit();
    }, []);
    const { data } = useListVariantConfiguratorsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            modelConfiguratorId: modelConfigurator?.id,
        },
    });

    const onSubmit = useHandleError<{
        oldVariantConfiguratorId: string;
        newVariantConfiguratorId: string;
    }>(
        async values => {
            const { data } = await apolloClient
                .mutate<CopyVariantConfiguratorMutation, CopyVariantConfiguratorMutationVariables>({
                    mutation: CopyVariantConfiguratorDocument,
                    variables: {
                        modelConfiguratorId: modelConfigurator.id,
                        oldVariantConfiguratorId: values.oldVariantConfiguratorId,
                        newVariantConfiguratorId: values.newVariantConfiguratorId,
                    },
                })
                .finally(() => {
                    setSubmitting(false);
                });

            if (data.variantConfigurator) {
                onSuccess(data?.variantConfigurator);
            }
        },
        [apolloClient, modelConfigurator.id, onSuccess]
    );

    const vehicleModuleId = useMemo(
        () =>
            modelConfigurator.module.__typename === ModuleType.ConfiguratorModule
                ? modelConfigurator.module.vehicleModuleId
                : null,
        [modelConfigurator.module]
    );

    return (
        <Modal
            cancelButtonProps={{ disabled: isSubmitting }}
            okButtonProps={{ loading: isSubmitting }}
            okText={t('modelConfiguratorDetails:actions.copyVariantOk')}
            onCancel={onCancel}
            onOk={handleOkay}
            title={t('modelConfiguratorDetails:modals.copyVariant.title')}
            visible={isVisible}
            width={480}
            centered
        >
            <Formik
                initialValues={{ oldVariantConfiguratorId: '', newVariantConfiguratorId: '' }}
                innerRef={formRef}
                onSubmit={onSubmit}
            >
                {({ handleSubmit }) => (
                    <Form
                        id={FORM_NAME}
                        name={FORM_NAME}
                        onSubmitCapture={ev => {
                            ev.preventDefault();
                            handleSubmit(ev);
                        }}
                    >
                        {/* TODO: add exception for already added variant */}
                        <VehicleSelectField
                            {...t('modelConfiguratorDetails:modals.copyVariant.fields.copyVariantFrom', {
                                returnObjects: true,
                            })}
                            dropdownOptions={data.lists.items}
                            modelIds={[]}
                            moduleId={vehicleModuleId}
                            name="oldVariantConfiguratorId"
                            vehicleKind={VehicleKind.LocalVariant}
                            useSuiteId
                        />
                        <VehicleSelectField
                            {...t('modelConfiguratorDetails:modals.copyVariant.fields.newVariant', {
                                returnObjects: true,
                            })}
                            modelIds={[modelConfigurator.model.id]}
                            moduleId={vehicleModuleId}
                            name="newVariantConfiguratorId"
                            vehicleKind={VehicleKind.LocalVariant}
                            useSuiteId
                        />
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default CopyVariantConfiguratorModal;
